import * as React from "react";
import { createClasses } from "@bloomberg/media-ui-core";

import Headline from "~components/shared/headline/Headline";
import { LiveBlogPosts } from "~components/shared/liveblog/LiveBlogPosts";
import {
    type MediaConfig,
    Media,
    MediaFormat,
    DEFAULT_MEDIA_PRIORITY,
} from "~components/shared/media/";
import OptionalEyebrow from "~components/shared/optional-eyebrow/OptionalEyebrow";
import Summary from "~components/shared/summary/Summary";
import { useLiveBlog } from "~hooks/useLiveBlog";
import {
    type CuratedItem,
    getByline,
    getKeypointsToggle,
    getMeta,
} from "~types/types";

import styles from "./liveblog.module.css";

const MEDIA_PRIORITY: MediaFormat[] = DEFAULT_MEDIA_PRIORITY;

const MEDIA_CONFIG: MediaConfig = {
    aspectRatio: 3 / 2,
    className: styles.image,
    desktopMaxWidth: 460,
    mobileMaxWidth: 715,
};

export interface LiveBlogItemProps {
    item: CuratedItem;
    renderingLocation?: string;
    className?: string;
    shouldShowImage?: boolean;
    withDek?: boolean;
    enableMediaCarousel?: boolean;
}

export function LiveBlogItem({
    item,
    className,
    shouldShowImage = true,
    withDek = false,
    enableMediaCarousel = false,
}: LiveBlogItemProps) {
    const { headline, labelDropdown, textLabel, url } = item;

    const byline = getByline(item);
    const meta = getMeta(item);
    const keypointsToggle = getKeypointsToggle(item);

    const { liveBlog, loadingStatus: liveBlogLoadingStatus } = useLiveBlog(
        meta?.terminalBlogId
    );
    const isComingUp =
        liveBlog?.state === "live" && liveBlog?.posts?.length === 1;

    return (
        <div className={createClasses(styles.liveblog, className)}>
            <OptionalEyebrow
                authorName={byline}
                liveBlogState={isComingUp ? "comingUp" : liveBlog?.state}
                textLabel={textLabel}
                presetLabel={labelDropdown}
            />
            <Headline isPhoenix text={headline} url={url} forceLinking />
            {withDek && (
                <Summary
                    className={styles.summary}
                    isPhoenix
                    summary={item.summary}
                />
            )}
            <div className={styles.group} data-component="liveblog-group">
                {shouldShowImage && (
                    <div
                        className={styles.media}
                        data-component="liveblog-media"
                    >
                        <Media
                            source={item}
                            priority={
                                enableMediaCarousel
                                    ? [MediaFormat.carousel, ...MEDIA_PRIORITY]
                                    : MEDIA_PRIORITY
                            }
                            config={MEDIA_CONFIG}
                        />
                    </div>
                )}
                <LiveBlogPosts
                    failed={liveBlogLoadingStatus.failed}
                    loaded={liveBlogLoadingStatus.loaded}
                    liveBlog={liveBlog}
                    url={url}
                    keypointsToggle={keypointsToggle}
                />
            </div>
        </div>
    );
}
